import { GoogleAuthProvider, OAuthProvider } from "firebase/auth";
import { getAuth, signInWithEmailAndPassword } from "firebase/auth";
import { signInWithPopup } from "firebase/auth";
import { useAppDispatch, useAppSelector } from "@packages/store/hooks";
import { loginUser } from "@packages/store/userSlice";
import logo from "@packages/assets/logo.png";
import arrow from "@packages/assets/arrow.png";
import bilfen from "@packages/assets/bilfen.jpg";
import isik from "@packages/assets/isik.png";
import itugv from "@packages/assets/itugv.png";
import ayseabla from "@packages/assets/ayseabla.png";
import sev from "@packages/assets/sev.png";
import tedAnkara from "@packages/assets/ted_ankara.svg";
import ted from "@packages/assets/ted.png";
import msu from "@packages/assets/msu.png";
import nun from "@packages/assets/nun.png";
import mba from "@packages/assets/mba.png";
import yetev from "@packages/assets/yetev.svg";
import mef from "@packages/assets/mef.png";
import ielev from "@packages/assets/ielev.jpg";
import cihangir from "@packages/assets/cihangir.png";
import mecOkullari from "@packages/assets/mecokullari.png";
import sahinkaya from "@packages/assets/sahinkaya.png";
import hisar from "@packages/assets/hisar.png";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useEffect, useState, useMemo } from "react";
import { Trans, useTranslation } from "react-i18next";
import { Button, Text, Input } from "@core/index";
import { AnimatePresence, motion } from "framer-motion";
import { useMobile } from "@packages/hooks/useMobile";
import { useTheme } from "@packages/hooks/useTheme";

// --- Refactored Configuration Logic ---

// Central configuration for each school/hostname
const SCHOOL_CONFIGS = {
  // Email Only Schools
  "bilfen.madlen.io": {
    showGoogleLogin: false,
    showMicrosoftLogin: false,
    showEmailLogin: true,
    logo: bilfen,
    logoHeight: "h-12",
  },
  "ielev.madlen.io": {
    showGoogleLogin: false,
    showMicrosoftLogin: false,
    showEmailLogin: true,
    logo: ielev,
    logoHeight: "h-12",
  },
  "ayseabla.madlen.io": {
    showGoogleLogin: false,
    showMicrosoftLogin: false,
    showEmailLogin: true,
    logo: ayseabla,
    logoHeight: "h-12",
  },
  "cihangir.madlen.io": {
    showMicrosoftLogin: false,
    showEmailLogin: true,
    logo: cihangir,
    logoHeight: "h-14",
  },
  "hisar.madlen.io": {
    showGoogleLogin: false,
    showMicrosoftLogin: false,
    showEmailLogin: true,
    logo: hisar,
    logoHeight: "h-14",
  },
  "msu.madlen.io": {
    showGoogleLogin: false,
    showMicrosoftLogin: false,
    showEmailLogin: true,
    logo: msu,
    logoHeight: "h-14",
  },
  "mecokullari.madlen.io": {
    showGoogleLogin: false,
    showMicrosoftLogin: false,
    showEmailLogin: true,
    logo: mecOkullari,
    logoHeight: "h-14",
  },

  // Social Logins Only Schools
  "yetev.madlen.io": {
    showGoogleLogin: true,
    showMicrosoftLogin: true,
    showEmailLogin: false,
    logo: yetev,
    logoHeight: "h-14",
  },

  // Schools with default login methods but custom logos
  "isik.madlen.io": { logo: isik, logoHeight: "h-12" },
  "itugvo.madlen.io": { logo: itugv, logoHeight: "h-28" },
  "sev.madlen.io": { logo: sev, logoHeight: "h-14" },
  "ted.madlen.io": { logo: ted, logoHeight: "h-14" },
  "tedankara.madlen.io": { logo: tedAnkara, logoHeight: "h-14" },
  "nun.madlen.io": { logo: nun, logoHeight: "h-14" },
  "mba.madlen.io": { logo: mba, logoHeight: "h-14" },
  "mef.madlen.io": { logo: mef, logoHeight: "h-14" },
  "sahinkaya.madlen.io": {
    logo: sahinkaya,
    showMicrosoftLogin: false,
    logoHeight: "h-14",
  },
};

const DEFAULT_CONFIG = {
  showGoogleLogin: true,
  showMicrosoftLogin: true,
  showEmailLogin: true,
  logo: null,
  logoHeight: "",
};

// Custom hook to get the login configuration based on the hostname
const useLoginConfig = () => {
  return useMemo(() => {
    const hostname = window.location.hostname;
    //const hostname = "cihangir.madlen.io"; // For testing
    const schoolConfig = SCHOOL_CONFIGS[hostname] || {};
    return { ...DEFAULT_CONFIG, ...schoolConfig };
  }, []);
};

// --- End of Refactored Configuration Logic ---

const LoginView = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const isAuthenticated = useAppSelector((state) => state.user.isAuthenticated);
  const { t, i18n } = useTranslation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const isMobile = useMobile();
  const { theme } = useTheme();
  const [searchParams] = useSearchParams();
  const redirect = searchParams.get("redirect");

  // Use the custom hook to get clean boolean flags
  const {
    showGoogleLogin,
    showMicrosoftLogin,
    showEmailLogin,
    logo: schoolLogo,
    logoHeight,
  } = useLoginConfig();

  // State to manage the visibility of the email form.
  // It's visible by default only if it's the *only* login option.
  const [isEmailFormVisible, setIsEmailFormVisible] = useState(
    showEmailLogin && !showGoogleLogin && !showMicrosoftLogin,
  );

  // Force light mode for this page
  useEffect(() => {
    // Remove dark mode class if it exists
    document.documentElement.classList.remove("dark");

    // Restore theme when component unmounts
    return () => {
      if (theme === "dark") {
        document.documentElement.classList.add("dark");
      }
    };
  }, [theme]);

  useEffect(() => {
    if (isAuthenticated) {
      const targetPath = redirect || `/dashboard?${searchParams.toString()}`;
      navigate(targetPath);
    }
  }, [isAuthenticated, navigate, redirect, searchParams]);

  const handleGoogleLogin = async () => {
    try {
      const provider = new GoogleAuthProvider();
      const auth = getAuth();
      const result = await signInWithPopup(auth, provider);
      const token = await result.user.getIdToken();
      const name = result.user.displayName || "";
      const surname = "";
      const avatar = result.user.photoURL || "";

      const response = await dispatch(
        loginUser({
          token,
          organizationId: 1,
          userType: "TEACHER",
          name,
          surname,
          language: i18n.language,
          avatar,
        }),
      );
      if (response.type === "fulfilled") {
        const targetPath = redirect || `/dashboard?${searchParams.toString()}`;
        navigate(targetPath);
      }
    } catch (error) {
      console.error("Google login failed:", error);
    }
  };

  const handleMicrosoftLogin = async () => {
    try {
      const provider = new OAuthProvider("microsoft.com");
      provider.setCustomParameters({
        prompt: "select_account",
      });
      const auth = getAuth();
      const result = await signInWithPopup(auth, provider);
      const token = await result.user.getIdToken();
      const name = result.user.displayName || "";
      const surname = "";
      const avatar = result.user.photoURL || "";

      const response = await dispatch(
        loginUser({
          token,
          organizationId: 1,
          userType: "TEACHER",
          name,
          surname,
          language: i18n.language,
          avatar,
        }),
      );
      if (response.type === "fulfilled") {
        const targetPath = redirect || `/dashboard?${searchParams.toString()}`;
        navigate(targetPath);
      }
    } catch (error) {
      console.error("Microsoft login failed:", error);
    }
  };

  const handleEmailLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);
    try {
      const auth = getAuth();
      const result = await signInWithEmailAndPassword(auth, email, password);
      const token = await result.user.getIdToken();
      const name = result.user.displayName || "";
      const surname = "";
      const avatar = result.user.photoURL || "";

      const response = await dispatch(
        loginUser({
          token,
          organizationId: 1,
          userType: "TEACHER",
          name,
          surname,
          language: i18n.language,
          avatar,
        }),
      );
      if (response.type === "fulfilled") {
        const targetPath = redirect || `/dashboard?${searchParams.toString()}`;
        navigate(targetPath);
      }
    } catch (error: any) {
      const errorMessage = error?.toString?.() || "";
      if (errorMessage.includes("auth/invalid-credential")) {
        setError(t("login.invalidCredentials"));
      } else {
        setError(t("login.loginFailed"));
      }
    } finally {
      setIsLoading(false);
    }
  };

  const toggleEmailForm = () => {
    setIsEmailFormVisible(!isEmailFormVisible);
    setError("");
  };

  const showSocialLogins = showGoogleLogin || showMicrosoftLogin;
  const showSeparator = showSocialLogins && showEmailLogin;

  return (
    <div className="min-h-screen flex flex-col lg:flex-row">
      {/* ----- Landing Page Content (Left side) - No changes needed here ----- */}
      <div className="w-full hidden lg:flex lg:w-3/5 bg-gradient-to-tr from-[#fae2c5] to-white p-10 items-center justify-center order-2 lg:order-1 shadow-2xl">
        <motion.div className="max-w-2xl">
          <div className="mb-4 text-center max-w-lg mx-auto">
            {!isMobile && (
              <>
                <div className="flex items-center justify-center mb-4 gap-3">
                  <div className="w-12 h-12 bg-white/10 backdrop-blur-md border-2 border-primary-300/50 text-white rounded-2xl flex items-center justify-center transform hover:scale-105 transition-all duration-300 shadow-[0_0_15px_rgba(234,88,12,0.3)]">
                    <img src={logo} alt="Madlen Logo" className="h-6" />
                  </div>
                  <Text
                    variant="h1"
                    size="2xl"
                    weight="bold"
                    className="text-gray-800 drop-shadow-md"
                  >
                    {t("login.landing.title")}
                  </Text>
                </div>
                <Text
                  variant="h2"
                  size="4xl"
                  weight="bold"
                  className="text- mb-3 drop-shadow-md"
                >
                  {t("login.landing.subtitle")}
                </Text>
              </>
            )}
            <Text variant="p" size="base" className="text-gray-700">
              {t("login.landing.description")}
            </Text>
          </div>

          <div className="space-y-6">
            <motion.div
              className="bg-gradient-to-r mr-20 from-blue-50 to-blue-100 p-6 rounded-2xl border-l-4 border-blue-500 hover:shadow-xl transition-all duration-300"
              initial={{ rotate: -0.6 }}
              animate={{
                rotate: [-3, 2, -3],
                x: [-10, 5, -10],
                y: [6, -4, 6],
              }}
              transition={{
                duration: 3,
                repeat: Infinity,
                ease: "easeInOut",
              }}
              whileHover={{ y: -8, rotate: 0, scale: 1.03 }}
            >
              <div className="flex items-center gap-6">
                <div className="flex-shrink-0 w-20 h-20 bg-blue-500 bg-opacity-10 rounded-full p-4 shadow-inner">
                  <svg viewBox="0 0 120 120" xmlns="http://www.w3.org/2000/svg">
                    <ellipse
                      cx="60"
                      cy="105"
                      rx="35"
                      ry="12"
                      fill="#2563EB"
                      opacity="0.3"
                    />

                    <rect
                      x="40"
                      y="50"
                      width="40"
                      height="55"
                      rx="8"
                      fill="#60A5FA"
                    />
                    <rect
                      x="42"
                      y="52"
                      width="36"
                      height="51"
                      rx="7"
                      fill="#93C5FD"
                    />

                    <rect
                      x="35"
                      y="20"
                      width="50"
                      height="40"
                      rx="10"
                      fill="#2563EB"
                    />
                    <rect
                      x="38"
                      y="23"
                      width="44"
                      height="34"
                      rx="8"
                      fill="#BFDBFE"
                    />

                    <rect
                      x="45"
                      y="30"
                      width="10"
                      height="13"
                      rx="3"
                      fill="#1E40AF"
                    />
                    <rect
                      x="65"
                      y="30"
                      width="10"
                      height="13"
                      rx="3"
                      fill="#1E40AF"
                    />
                    <rect
                      x="47"
                      y="32"
                      width="6"
                      height="9"
                      rx="2"
                      fill="#93C5FD"
                    />
                    <rect
                      x="67"
                      y="32"
                      width="6"
                      height="9"
                      rx="2"
                      fill="#93C5FD"
                    />

                    <rect
                      x="45"
                      y="10"
                      width="4"
                      height="10"
                      rx="2"
                      fill="#2563EB"
                    />
                    <rect
                      x="70"
                      y="10"
                      width="4"
                      height="10"
                      rx="2"
                      fill="#2563EB"
                    />
                    <circle cx="47" cy="7" r="4" fill="#93C5FD" />
                    <circle cx="72" cy="7" r="4" fill="#93C5FD" />

                    <rect
                      x="25"
                      y="60"
                      width="15"
                      height="5"
                      rx="2"
                      fill="#2563EB"
                    />
                    <rect
                      x="15"
                      y="57"
                      width="10"
                      height="10"
                      rx="5"
                      fill="#93C5FD"
                    />
                    <rect
                      x="80"
                      y="60"
                      width="15"
                      height="5"
                      rx="2"
                      fill="#2563EB"
                    />
                    <rect
                      x="95"
                      y="57"
                      width="10"
                      height="10"
                      rx="5"
                      fill="#93C5FD"
                    />

                    <rect
                      x="45"
                      y="105"
                      width="10"
                      height="15"
                      rx="2"
                      fill="#2563EB"
                    />
                    <rect
                      x="65"
                      y="105"
                      width="10"
                      height="15"
                      rx="2"
                      fill="#2563EB"
                    />
                    <ellipse cx="50" cy="120" rx="5" ry="2" fill="#2563EB" />
                    <ellipse cx="70" cy="120" rx="5" ry="2" fill="#2563EB" />

                    <rect
                      x="50"
                      y="75"
                      width="20"
                      height="10"
                      rx="2"
                      fill="#1E40AF"
                    />
                    <circle cx="55" cy="80" r="2" fill="#BFDBFE" />
                    <circle cx="60" cy="80" r="2" fill="#FCD34D" />
                    <circle cx="65" cy="80" r="2" fill="#BFDBFE" />
                  </svg>
                </div>
                <div>
                  <Text
                    variant="h3"
                    size="lg"
                    weight="semibold"
                    className="text-blue-800 drop-shadow-sm"
                  >
                    {t("login.landing.features.feature1.title")}
                  </Text>
                </div>
              </div>
            </motion.div>

            <motion.div
              className="bg-gradient-to-r ml-20 from-purple-50 to-purple-100 p-6 rounded-2xl border-r-4 border-purple-500 hover:shadow-xl transition-all duration-300"
              initial={{ rotate: 1 }}
              animate={{
                rotate: [3, -2, 3],
                x: [9, -5, 9],
                y: [-8, 5, -8],
              }}
              transition={{
                duration: 2.5,
                repeat: Infinity,
                ease: "easeInOut",
              }}
              whileHover={{ y: -8, rotate: 0, scale: 1.03 }}
            >
              <div className="flex items-center flex-row-reverse gap-6">
                <div className="flex-shrink-0 w-20 h-20 bg-purple-500 bg-opacity-10 rounded-full p-4 shadow-inner">
                  <svg viewBox="0 0 120 120" xmlns="http://www.w3.org/2000/svg">
                    <path d="M20,40 L60,30 L60,80 L20,90 Z" fill="#9061F9" />
                    <path d="M60,30 L100,40 L100,90 L60,80 Z" fill="#7C3AED" />
                    <path
                      d="M20,40 L60,30 L100,40"
                      fill="none"
                      stroke="#4C1D95"
                      strokeWidth="2"
                    />
                    <path
                      d="M60,30 L60,80"
                      fill="none"
                      stroke="#F5F3FF"
                      strokeWidth="2"
                    />
                    <path
                      d="M30,45 L50,40"
                      fill="none"
                      stroke="#E9D5FD"
                      strokeWidth="1"
                    />
                    <path
                      d="M30,55 L50,50"
                      fill="none"
                      stroke="#E9D5FD"
                      strokeWidth="1"
                    />
                    <path
                      d="M30,65 L50,60"
                      fill="none"
                      stroke="#E9D5FD"
                      strokeWidth="1"
                    />
                    <path
                      d="M30,75 L50,70"
                      fill="none"
                      stroke="#E9D5FD"
                      strokeWidth="1"
                    />
                    <path
                      d="M70,40 L90,45"
                      fill="none"
                      stroke="#E9D5FD"
                      strokeWidth="1"
                    />
                    <path
                      d="M70,50 L90,55"
                      fill="none"
                      stroke="#E9D5FD"
                      strokeWidth="1"
                    />
                    <path
                      d="M70,60 L90,65"
                      fill="none"
                      stroke="#E9D5FD"
                      strokeWidth="1"
                    />
                    <path
                      d="M70,70 L90,75"
                      fill="none"
                      stroke="#E9D5FD"
                      strokeWidth="1"
                    />
                    <circle cx="80" cy="15" r="10" fill="#F0E1FF" />{" "}
                    <path
                      d="M80,25 L80,35 L70,50 L65,45"
                      fill="none"
                      stroke="#9061F9"
                      strokeWidth="3"
                    />{" "}
                    <path
                      d="M80,25 L80,50"
                      fill="none"
                      stroke="#9061F9"
                      strokeWidth="3"
                    />{" "}
                    <circle cx="65" cy="45" r="4" fill="#F0E1FF" />
                    <rect
                      x="15"
                      y="90"
                      width="90"
                      height="5"
                      rx="2"
                      fill="#4C1D95"
                    />
                    <rect x="20" y="95" width="5" height="15" fill="#6D28D9" />
                    <rect x="95" y="95" width="5" height="15" fill="#6D28D9" />
                    <path
                      d="M90,15 L100,40 L105,42 L102,45 L95,43 L85,20"
                      fill="#F0E1FF"
                    />
                    <rect
                      x="87"
                      y="10"
                      width="6"
                      height="10"
                      rx="2"
                      fill="#9061F9"
                    />
                    <circle cx="102" cy="43" r="3" fill="#F9FAFB" />
                  </svg>
                </div>
                <div>
                  <Text
                    variant="h3"
                    size="lg"
                    weight="semibold"
                    className="text-purple-800 drop-shadow-sm"
                  >
                    {t("login.landing.features.feature2.title")}
                  </Text>
                </div>
              </div>
            </motion.div>

            <motion.div
              className="bg-gradient-to-r mr-20 from-green-50 to-green-100 p-6 rounded-2xl border-l-4 border-green-500 hover:shadow-xl transition-all duration-300"
              initial={{ rotate: -1 }}
              animate={{
                rotate: [-2, 3, -2],
                x: [-8, 8, -8],
                y: [7, -6, 7],
              }}
              transition={{
                duration: 3,
                repeat: Infinity,
                ease: "easeInOut",
              }}
              whileHover={{ y: -8, rotate: 0, scale: 1.03 }}
            >
              <div className="flex items-center gap-6">
                <div className="flex-shrink-0 w-20 h-20 bg-green-500 bg-opacity-10 rounded-full p-4 shadow-inner">
                  <svg viewBox="0 0 120 120" xmlns="http://www.w3.org/2000/svg">
                    <ellipse
                      cx="60"
                      cy="100"
                      rx="40"
                      ry="8"
                      fill="#059669"
                      opacity="0.2"
                    />{" "}
                    <circle
                      cx="65"
                      cy="50"
                      r="25"
                      fill="#D1FAE5"
                      stroke="#059669"
                      strokeWidth="3"
                    />
                    <circle cx="65" cy="50" r="20" fill="#ECFDF5" />
                    <rect
                      x="80"
                      y="70"
                      width="8"
                      height="25"
                      rx="4"
                      fill="#059669"
                      transform="rotate(-45, 85, 75)"
                    />
                    <rect
                      x="82"
                      y="72"
                      width="4"
                      height="21"
                      rx="2"
                      fill="#10B981"
                      transform="rotate(-45, 85, 75)"
                    />
                    <rect
                      x="30"
                      y="40"
                      width="30"
                      height="40"
                      rx="4"
                      fill="#A7F3D0"
                    />
                    <rect
                      x="33"
                      y="44"
                      width="24"
                      height="5"
                      rx="1"
                      fill="#059669"
                    />
                    <rect
                      x="33"
                      y="53"
                      width="24"
                      height="2"
                      rx="1"
                      fill="#059669"
                    />
                    <rect
                      x="33"
                      y="58"
                      width="18"
                      height="2"
                      rx="1"
                      fill="#059669"
                    />
                    <rect
                      x="33"
                      y="63"
                      width="21"
                      height="2"
                      rx="1"
                      fill="#059669"
                    />
                    <rect
                      x="33"
                      y="68"
                      width="15"
                      height="2"
                      rx="1"
                      fill="#059669"
                    />
                    <rect
                      x="33"
                      y="73"
                      width="22"
                      height="2"
                      rx="1"
                      fill="#059669"
                    />
                    <rect
                      x="35"
                      y="35"
                      width="30"
                      height="40"
                      rx="4"
                      fill="#D1FAE5"
                      transform="rotate(-5, 50, 55)"
                    />
                    <rect
                      x="38"
                      y="39"
                      width="24"
                      height="5"
                      rx="1"
                      fill="#059669"
                      transform="rotate(-5, 50, 55)"
                    />
                    <rect
                      x="38"
                      y="48"
                      width="24"
                      height="2"
                      rx="1"
                      fill="#059669"
                      transform="rotate(-5, 50, 55)"
                    />
                    <rect
                      x="38"
                      y="53"
                      width="18"
                      height="2"
                      rx="1"
                      fill="#059669"
                      transform="rotate(-5, 50, 55)"
                    />
                    <rect
                      x="38"
                      y="58"
                      width="21"
                      height="2"
                      rx="1"
                      fill="#059669"
                      transform="rotate(-5, 50, 55)"
                    />
                    <rect
                      x="38"
                      y="63"
                      width="15"
                      height="2"
                      rx="1"
                      fill="#059669"
                      transform="rotate(-5, 50, 55)"
                    />
                    <rect
                      x="38"
                      y="68"
                      width="22"
                      height="2"
                      rx="1"
                      fill="#059669"
                      transform="rotate(-5, 50, 55)"
                    />
                    <rect
                      x="25"
                      y="30"
                      width="30"
                      height="40"
                      rx="4"
                      fill="#ECFDF5"
                      transform="rotate(5, 40, 50)"
                    />
                    <rect
                      x="28"
                      y="34"
                      width="24"
                      height="5"
                      rx="1"
                      fill="#10B981"
                      transform="rotate(5, 40, 50)"
                    />
                    <rect
                      x="28"
                      y="43"
                      width="24"
                      height="2"
                      rx="1"
                      fill="#10B981"
                      transform="rotate(5, 40, 50)"
                    />
                    <rect
                      x="28"
                      y="48"
                      width="18"
                      height="2"
                      rx="1"
                      fill="#10B981"
                      transform="rotate(5, 40, 50)"
                    />
                    <rect
                      x="28"
                      y="53"
                      width="21"
                      height="2"
                      rx="1"
                      fill="#10B981"
                      transform="rotate(5, 40, 50)"
                    />
                    <rect
                      x="28"
                      y="58"
                      width="15"
                      height="2"
                      rx="1"
                      fill="#10B981"
                      transform="rotate(5, 40, 50)"
                    />
                    <rect
                      x="28"
                      y="63"
                      width="22"
                      height="2"
                      rx="1"
                      fill="#10B981"
                      transform="rotate(5, 40, 50)"
                    />
                  </svg>
                </div>
                <div>
                  <Text
                    variant="h3"
                    size="lg"
                    weight="semibold"
                    className="text-green-800 drop-shadow-sm"
                  >
                    {t("login.landing.features.feature3.title")}
                  </Text>
                </div>
              </div>
            </motion.div>
          </div>
        </motion.div>
      </div>

      {/* ----- Login Form (Right side) - Refactored ----- */}
      <div className="relative w-full h-screen lg:h-auto lg:w-2/5 p-8 bg-gradient-to-tl from-[#fae2c5] to-white flex items-center justify-center order-1 lg:order-2">
        <div className="hidden lg:flex items-center justify-center">
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="absolute top-1/2 -left-24 -translate-y-1/2"
          >
            <img src={arrow} alt="Arrow" className="h-28 rotate-[-40deg]" />
          </motion.div>
        </div>
        <div className="w-full max-w-md">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="space-y-4 bg-white/50 rounded-2xl p-8 shadow-xl"
          >
            {isMobile && (
              <div className="flex items-center justify-center mb-8 gap-3">
                <div className="w-12 h-12 bg-white/10 backdrop-blur-md border-2 border-primary-300/50 text-white rounded-2xl flex items-center justify-center transform hover:scale-105 transition-all duration-300 shadow-[0_0_15px_rgba(234,88,12,0.3)]">
                  <img src={logo} alt="Madlen Logo" className="h-6" />
                </div>
                <Text
                  variant="h1"
                  size="2xl"
                  weight="bold"
                  className="text-gray-800 drop-shadow-md text-center"
                >
                  {t("login.landing.title")}
                </Text>
              </div>
            )}

            {/* Cleaner logo rendering */}
            {schoolLogo && (
              <img
                src={schoolLogo}
                alt="School Logo"
                className={`${logoHeight} mx-auto mb-6`}
              />
            )}

            {!isMobile && (
              <Text
                variant={"h1"}
                size={"3xl"}
                weight={"bold"}
                className="text-gray-800 text-center"
              >
                {t("login.signIn")}
              </Text>
            )}

            {/* Conditional rendering based on clean booleans */}
            {showGoogleLogin && (
              <motion.div whileTap={{ scale: 0.95 }} className="w-full">
                <Button
                  onClick={handleGoogleLogin}
                  bgColor="bg-white"
                  rounded="lg"
                  textColor="text-gray-700"
                  size="md"
                  fullWidth
                  className="border border-gray-200 hover:bg-gray-50 transition-all"
                >
                  <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                    <path
                      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                      fill="#4285F4"
                    />
                    <path
                      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                      fill="#34A853"
                    />
                    <path
                      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                      fill="#FBBC05"
                    />
                    <path
                      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                      fill="#EA4335"
                    />
                  </svg>
                  {t("login.google")}
                </Button>
              </motion.div>
            )}

            {showMicrosoftLogin && (
              <motion.div whileTap={{ scale: 0.95 }} className="w-full">
                <Button
                  onClick={handleMicrosoftLogin}
                  bgColor="bg-white"
                  rounded="lg"
                  textColor="text-gray-700"
                  fullWidth
                  size="md"
                  className="border border-gray-200 hover:bg-gray-50 transition-all"
                >
                  <svg className="w-5 h-5 mr-2" viewBox="0 0 23 23" fill="none">
                    <path fill="#F25022" d="M10.5 10.5H0.5V0.5h10v10z" />
                    <path fill="#00A4EF" d="M10.5 21.5H0.5V11.5h10v10z" />
                    <path fill="#7FBA00" d="M21.5 10.5H11.5V0.5h10v10z" />
                    <path fill="#FFB900" d="M21.5 21.5H11.5V11.5h10v10z" />
                  </svg>
                  {t("login.microsoft")}
                </Button>
              </motion.div>
            )}

            {showSeparator && (
              <div className="relative py-3">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-200"></div>
                </div>
                <div className="relative flex justify-center">
                  <span className="px-3 text-sm text-gray-500 bg-white/50">
                    {t("login.or")}
                  </span>
                </div>
              </div>
            )}

            {showEmailLogin && (
              <AnimatePresence mode="wait">
                {isEmailFormVisible ? (
                  <motion.form
                    key="email-login-form"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.2 }}
                    onSubmit={handleEmailLogin}
                    className="space-y-4"
                  >
                    <div>
                      <Text
                        variant="p"
                        size="sm"
                        className="mb-1 text-gray-700 font-medium"
                      >
                        {t("login.email_label")}
                      </Text>
                      <Input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder={t("login.email_placeholder")}
                        required
                        classNames={{
                          input:
                            "w-full px-4 py-2 rounded-lg border border-gray-200 focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none transition",
                        }}
                      />
                    </div>
                    <div>
                      <Text
                        variant="p"
                        size="sm"
                        className="mb-1 text-gray-700 font-medium"
                      >
                        {t("login.password")}
                      </Text>
                      <Input
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder={t("login.password_placeholder")}
                        required
                        classNames={{
                          input:
                            "w-full px-4 py-2 rounded-lg border border-gray-200 focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none transition",
                        }}
                      />
                    </div>
                    {error && (
                      <Text variant="p" size="sm" className="text-red-500">
                        {error}
                      </Text>
                    )}
                    <motion.div whileTap={{ scale: 0.98 }} className="w-full">
                      <Button
                        type="submit"
                        bgColor="bg-primary-500"
                        rounded="lg"
                        textColor="text-white"
                        fullWidth
                        size="md"
                        disabled={isLoading}
                        className="shadow-md hover:shadow-lg transition-all disabled:opacity-70 disabled:cursor-not-allowed"
                      >
                        <div className="flex items-center justify-center">
                          {isLoading && (
                            <svg
                              className="animate-spin -ml-1 mr-2 h-5 w-5 text-white"
                              xmlns="http://www.w3.org/2000/svg"
                              fill="none"
                              viewBox="0 0 24 24"
                            >
                              <circle
                                className="opacity-25"
                                cx="12"
                                cy="12"
                                r="10"
                                stroke="currentColor"
                                strokeWidth="4"
                              ></circle>
                              <path
                                className="opacity-75"
                                fill="currentColor"
                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                              ></path>
                            </svg>
                          )}
                          {t("login.submit")}
                        </div>
                      </Button>
                    </motion.div>
                  </motion.form>
                ) : (
                  <motion.div
                    key="email-login-button"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.2 }}
                  >
                    <Button
                      onClick={toggleEmailForm}
                      bgColor="bg-primary-500"
                      rounded="xl"
                      textColor="text-white"
                      fullWidth
                      size="md"
                      className="shadow-lg backdrop-blur-sm hover:shadow-primary-500/25 hover:shadow-xl hover:transform hover:scale-102"
                    >
                      <svg
                        className="w-5 h-5 mr-2"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                        <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                      </svg>
                      {t("login.email")}
                    </Button>
                  </motion.div>
                )}
              </AnimatePresence>
            )}

            <div className="pt-4">
              <Text variant="p" size="xs" className="text-gray-500 text-center">
                <Trans i18nKey="login.privacy">
                  <Link
                    target="_blank"
                    className="text-blue-500"
                    to="https://deploy.madlen.io/privacy.html"
                  ></Link>
                </Trans>
              </Text>
            </div>

            <div className="mt-6">
              <div className="mt-4 flex justify-center gap-4">
                <Button
                  onClick={() => {
                    i18n.changeLanguage("en");
                  }}
                  rounded="md"
                  size="md"
                  bgColor={`${
                    (localStorage.getItem("i18nextLng") || "en") === "en"
                      ? "bg-primary-500"
                      : "bg-gray-500"
                  }`}
                  textColor={`${
                    (localStorage.getItem("i18nextLng") || "en") === "en"
                      ? "text-white"
                      : "text-white"
                  }`}
                  className={`${
                    (localStorage.getItem("i18nextLng") || "en") === "en"
                      ? "shadow-lg hover:shadow-primary-500/25 hover:shadow-xl"
                      : "backdrop-blur-sm"
                  }`}
                >
                  English
                </Button>
                <Button
                  onClick={() => {
                    i18n.changeLanguage("tr");
                  }}
                  rounded="md"
                  size="md"
                  bgColor={`${
                    (localStorage.getItem("i18nextLng") || "tr") === "tr"
                      ? "bg-primary-500"
                      : "bg-gray-500"
                  }`}
                  textColor={`${
                    (localStorage.getItem("i18nextLng") || "tr") === "tr"
                      ? "text-white"
                      : "text-white"
                  }`}
                  className={`${
                    (localStorage.getItem("i18nextLng") || "tr") === "tr"
                      ? "shadow-lg hover:shadow-primary-500/25 hover:shadow-xl"
                      : "backdrop-blur-sm"
                  }`}
                >
                  Türkçe
                </Button>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default LoginView;
